<script setup>
// NOTICE: KAPTION-INTEGRATION — Unified search field for the reports dashboard.
// The free-text companion to KaptionFilterSelect: it shares the IDENTICAL pill
// chrome (same border, radius, height, focus ring, hover, tokens) so a search
// box sits seamlessly beside the inbox / campaign / date dropdowns in the
// reports toolbar. Leading search icon, debounced `search` event, inline clear
// button, optional loading spinner.
//
// kaption-platform-style.SEARCH.* — see KAPTION_PLATFORM_STYLE.md
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  // v-model — the current query string.
  modelValue: {
    type: String,
    default: '',
  },
  // Falls back to a generic i18n placeholder when empty.
  placeholder: {
    type: String,
    default: '',
  },
  // Leading icon (swapped for a spinner while `loading`).
  icon: {
    type: String,
    default: 'i-lucide-search',
  },
  // 'sm' matches the filter pills (toolbar); 'md' is roomier for empty states.
  size: {
    type: String,
    default: 'sm',
    validator: value => ['sm', 'md'].includes(value),
  },
  // Tailwind width class — explicit so it never collides with a fallthrough
  // class. Pass 'w-full' to fill a container.
  widthClass: {
    type: String,
    default: 'w-56',
  },
  // Debounce (ms) before the `search` event fires. 0 = fire on every keystroke.
  debounce: {
    type: Number,
    default: 250,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  // Show the inline clear (✕) button once there's a query.
  clearable: {
    type: Boolean,
    default: true,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'search', 'clear', 'enter']);

const { t } = useI18n();

const inputRef = ref(null);
const internalValue = ref(props.modelValue);
let debounceTimer = null;

const sizeClasses = computed(() =>
  props.size === 'md'
    ? {
        wrapper: 'h-9 text-sm gap-2',
        pad: 'pl-9 pr-9',
        lead: 'left-3 size-4',
        clear: 'right-2.5 size-4',
      }
    : {
        wrapper: 'h-8 text-xs gap-2',
        pad: 'pl-8 pr-8',
        lead: 'left-2.5 size-3.5',
        clear: 'right-2 size-3.5',
      }
);

const resolvedPlaceholder = computed(
  () => props.placeholder || t('CAMPAIGN.REPORT.DASHBOARD.SEARCH_PLACEHOLDER')
);

const showClear = computed(
  () => props.clearable && !props.disabled && internalValue.value.length > 0
);

const flushSearch = () => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  emit('search', internalValue.value);
};

const scheduleSearch = () => {
  if (props.debounce <= 0) {
    emit('search', internalValue.value);
    return;
  }
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    emit('search', internalValue.value);
  }, props.debounce);
};

const onInput = event => {
  internalValue.value = event.target.value;
  emit('update:modelValue', internalValue.value);
  scheduleSearch();
};

const onEnter = () => {
  flushSearch();
  emit('enter', internalValue.value);
};

const clear = () => {
  internalValue.value = '';
  emit('update:modelValue', '');
  emit('clear');
  flushSearch();
  inputRef.value?.focus();
};

// Keep in sync when the parent resets the model externally.
watch(
  () => props.modelValue,
  newValue => {
    if (newValue !== internalValue.value) internalValue.value = newValue;
  }
);

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer);
});
</script>

<template>
  <div
    class="relative flex items-center rounded-lg border bg-n-alpha-black2 transition-colors focus-within:border-n-brand"
    :class="[
      widthClass,
      sizeClasses.wrapper,
      disabled
        ? 'opacity-50 cursor-not-allowed border-n-weak'
        : 'border-n-weak hover:border-n-slate-7',
    ]"
  >
    <!-- Leading icon / loading spinner -->
    <span
      class="absolute text-n-slate-10 pointer-events-none"
      :class="[
        sizeClasses.lead,
        loading ? 'i-lucide-loader-circle animate-spin' : icon,
      ]"
    />

    <input
      ref="inputRef"
      :value="internalValue"
      type="search"
      :placeholder="resolvedPlaceholder"
      :disabled="disabled"
      :autofocus="autofocus"
      class="reset-base w-full h-full bg-transparent border-none font-medium text-n-slate-12 placeholder:text-n-slate-10 placeholder:font-normal focus:outline-none"
      :class="sizeClasses.pad"
      @input="onInput"
      @keydown.enter.prevent="onEnter"
    />

    <!-- Inline clear -->
    <button
      v-if="showClear"
      type="button"
      tabindex="-1"
      class="absolute flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 transition-colors i-lucide-x"
      :class="sizeClasses.clear"
      :aria-label="t('CAMPAIGN.REPORT.DASHBOARD.FILTER_CLEAR')"
      @click="clear"
    />
  </div>
</template>
